import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { googleReferred } from '../utils/adAttribution';

/**
 * Which paywall offer the web funnel's Pro card makes, decided by a PostHog
 * multivariate feature flag so the experiment can be run from the PostHog UI
 * without a deploy. The arms themselves are priced by the backend
 * (core/config.py FUNNEL_OFFER_VARIANTS), the page only names the arm.
 *
 *   control — $7.99 for the first month
 *   lower   — $4.99 for the first month
 *   pwyw    — pay what you want: the parent names the first month's price
 *
 * The first year is the same in every arm. The flag in PostHog: key
 * `funnel-paywall-offer`, variants named exactly as above.
 *
 * Same rules as the moments price test (make/priceTest.js). Anything that
 * isn't an arm the flag dealt this visit (the flag off or answering something
 * else, PostHog not running or slower than a second, a visit from a Google ad)
 * is "not in the test": the card shows the funnel's own offer (control's
 * price), checkout names no arm, and the page keeps the visit out of the
 * experiment's exposures (FunnelAuthPage: `offer_in_test`). It is never
 * reported as control. A visit timed out of the test stays out even when the
 * flags land later, so the price never changes under the parent.
 *
 * Pinned in sessionStorage once decided ('none' for not in the test): the
 * Google round trip reloads the page and sign-in can re-bucket the visitor,
 * and a parent must never see the price change under them. There is no URL
 * override: the flag (or the pinned session value) is the only way into an
 * arm, so a shared link can never pick a price.
 *
 * A visitor from a Google ad (utils/adAttribution.googleReferred) is never put
 * in the experiment: the ads state control's price, and Google's pricing
 * policy wants the page to charge what the ad said. Decided before the flag
 * is read.
 */

export const OFFER_FLAG_KEY = 'funnel-paywall-offer';
export const OFFER_VARIANTS = ['control', 'lower', 'pwyw'];
export const OFFER_VARIANT_KEY = 'rc_funnel_offer_variant';
export const OFFER_FLAG_TIMEOUT_MS = 1000;
const OUT = 'none';   // pinned for "not in the test"

const asVariant = (value) => (OFFER_VARIANTS.includes(value) ? value : null);

// '' = pinned out of the test, an arm, or null when nothing is pinned
const readPinned = () => {
  try {
    const value = sessionStorage.getItem(OFFER_VARIANT_KEY);
    return value === OUT ? '' : asVariant(value);
  } catch (_) {
    return null;
  }
};

const pin = (variant) => {
  try {
    sessionStorage.setItem(OFFER_VARIANT_KEY, variant || OUT);
  } catch (_) {
    // not pinned across a reload; the flag decides again
  }
};

// Reading the flag is what has posthog-js emit the `$feature_flag_called`
// event; only ever called while this visit can still join the test
const readFlag = () => {
  try {
    return asVariant(posthog.getFeatureFlag(OFFER_FLAG_KEY));
  } catch (_) {
    return null;
  }
};

const flagsReady = () => {
  try {
    return !!posthog.featureFlags?.hasLoadedFlags;
  } catch (_) {
    return false;
  }
};

/**
 * @returns {string|null} The arm ('control' | 'lower' | 'pwyw'), '' when this
 *   visit is not in the test, or null for up to a second while PostHog's first
 *   flag response is awaited (the paywall is many screens in, so in practice
 *   the answer is known long before a price is shown).
 */
export const useOfferVariant = () => {
  const [variant, setVariant] = useState(() => {
    if (googleReferred()) {
      pin('');
      return '';
    }
    const pinned = readPinned();
    if (pinned !== null) return pinned;
    if (!posthog.__loaded) {
      pin('');
      return '';
    }
    const now = readFlag();
    if (now || flagsReady()) {
      pin(now || '');
      return now || '';
    }
    return null;
  });

  useEffect(() => {
    if (variant !== null) return undefined;
    let settled = false;
    const settle = (next) => {
      if (settled) return;
      settled = true;
      pin(next);
      setVariant(next);
    };
    let unsubscribe = () => {};
    const timer = setTimeout(() => settle(''), OFFER_FLAG_TIMEOUT_MS);
    try {
      unsubscribe = posthog.onFeatureFlags(() => {
        if (settled) return;   // too late: a timed-out visit stays out
        settle(readFlag() || '');
      }) || unsubscribe;
    } catch (_) {
      settle('');
    }
    return () => {
      clearTimeout(timer);
      try {
        unsubscribe();
      } catch (_) {
        // ignore
      }
    };
  }, [variant]);

  return variant;
};

export default useOfferVariant;
